import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { useQuery, useQueryClient } from 'react-query';
import { useSSEAwareQuery } from '../../hooks/useSSEAwareQuery';
import {
  createSchedule,
  deleteSchedule,
  listSchedules,
  toggleSchedule,
  triggerSchedule,
  updateSchedule,
  Schedule,
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
} from '../../services/scheduleApi';
import CronExpressionInput from './CronExpressionInput';
import ScheduleRunHistory from './ScheduleRunHistory';
// import IncrementalStatePanel from './IncrementalStatePanel';

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
  completed: 'success',
  failed: 'error',
  running: 'info',
  skipped: 'warning',
};

function formatDate(iso?: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface ScheduleManagerProps {
  projectId: string;
  batchId?: string;
}

export default function ScheduleManager({ projectId, batchId }: ScheduleManagerProps) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editSchedule, setEditSchedule] = useState<Schedule | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { data: schedules, isLoading } = useSSEAwareQuery(
    ['schedules', projectId],
    () => listSchedules(projectId),
    { pollingInterval: 15_000, ssePollingInterval: 60_000 },
  );

  const invalidate = () => queryClient.invalidateQueries(['schedules', projectId]);

  const handleToggle = async (schedule: Schedule) => {
    await toggleSchedule(schedule.schedule_id, !schedule.is_enabled);
    invalidate();
  };

  const handleTrigger = async (scheduleId: string) => {
    await triggerSchedule(scheduleId);
    invalidate();
  };

  const handleDelete = async (scheduleId: string) => {
    await deleteSchedule(scheduleId);
    invalidate();
  };

  const hasSchedules = schedules && schedules.length > 0;

  return (
    <Box>
      {/* Loading */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={28} />
        </Box>
      ) : !hasSchedules ? (
        /* ── Empty state ── */
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 10,
            px: 4,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'action.hover',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom>
            No schedules yet
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 360, textAlign: 'center', mb: 3 }}
          >
            Automate your pipeline by scheduling recurring runs. Choose a frequency,
            load mode, and let Migratr handle the rest.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create Schedule
          </Button>
        </Box>
      ) : (
        /* ── Schedule list ── */
        <>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ScheduleIcon color="primary" />
          <Typography variant="h6">Schedules</Typography>
        </Stack>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Schedule
        </Button>
      </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Name</TableCell>
                <TableCell>Frequency</TableCell>
                <TableCell>Next Run</TableCell>
                <TableCell>Last Status</TableCell>
                <TableCell>Enabled</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedules.map((s: Schedule) => (
                <React.Fragment key={s.schedule_id}>
                  <TableRow hover>
                    <TableCell sx={{ width: 32 }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setExpandedId(expandedId === s.schedule_id ? null : s.schedule_id)
                        }
                      >
                        {expandedId === s.schedule_id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {s.name}
                        </Typography>
                        {(s as any).connector_status === 'missing' && (
                          <Tooltip title="Source connector was deleted. Edit schedule to reconfigure.">
                            <Chip label="⚠ Connector" size="small" color="warning" sx={{ fontSize: 9, height: 18 }} />
                          </Tooltip>
                        )}
                      </Box>
                      {(s as any).connector_name && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          Source: {(s as any).connector_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.cron_description || s.cron_expression}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{formatDate(s.next_run_at)}</TableCell>
                    <TableCell>
                      {s.last_run_status ? (
                        <Chip
                          label={s.last_run_status}
                          size="small"
                          color={STATUS_COLORS[s.last_run_status] || 'default'}
                          variant="outlined"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={s.is_enabled}
                        onChange={() => handleToggle(s)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={s.last_run_status === 'running' ? 'A run is already in progress' : 'Run now'}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleTrigger(s.schedule_id)}
                              disabled={s.last_run_status === 'running'}
                            >
                              <PlayArrowIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setEditSchedule(s)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(s.schedule_id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expandedId === s.schedule_id ? undefined : 'none' }}>
                      <Collapse in={expandedId === s.schedule_id} unmountOnExit>
                        <Box py={2}>
                          <Typography variant="subtitle2" mb={1}>
                            Run History
                          </Typography>
                          <ScheduleRunHistory scheduleId={s.schedule_id} projectId={projectId} />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      </>
      )}

      {/* Create Dialog */}
      <ScheduleDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        projectId={projectId}
        batchId={batchId}
        onSaved={invalidate}
      />

      {/* Edit Dialog */}
      {editSchedule && (
        <ScheduleDialog
          open
          onClose={() => setEditSchedule(null)}
          projectId={projectId}
          existing={editSchedule}
          onSaved={invalidate}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit Dialog
// ---------------------------------------------------------------------------

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  batchId?: string;
  existing?: Schedule;
  onSaved: () => void;
}

function ScheduleDialog({ open, onClose, projectId, batchId, existing, onSaved }: ScheduleDialogProps) {
  const [name, setName] = useState(existing?.name || '');
  const [cron, setCron] = useState(existing?.cron_expression || '0 0 * * *');
  const [loadMode, setLoadMode] = useState(existing?.load_mode || 'full');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const existingMeta = (existing?.metadata || {}) as Record<string, any>;
  const [stagingMode, setStagingMode] = useState<string>(existingMeta.staging_mode || 'auto');
  const [watermarkColumn, setWatermarkColumn] = useState<string>(existingMeta.incremental?.watermark_column || '');
  const [watermarkType, setWatermarkType] = useState<string>(existingMeta.incremental?.watermark_type || 'timestamp');

  const isEdit = !!existing;

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const metadata: Record<string, any> = {
        staging_mode: stagingMode,
      };
      if (loadMode === 'incremental' && watermarkColumn) {
        metadata.incremental = {
          enabled: true,
          watermark_column: watermarkColumn,
          watermark_type: watermarkType,
        };
      }
      if (isEdit) {
        const payload: ScheduleUpdateRequest = { metadata };
        if (name !== existing!.name) payload.name = name;
        if (cron !== existing!.cron_expression) payload.cron_expression = cron;
        if (loadMode !== existing!.load_mode) payload.load_mode = loadMode;
        await updateSchedule(existing!.schedule_id, payload);
      } else {
        const payload: ScheduleCreateRequest = {
          project_id: projectId,
          name,
          cron_expression: cron,
          load_mode: loadMode,
          source_batch_id: batchId,
          metadata,
        };
        await createSchedule(payload);
      }
      onSaved();
      onClose();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to save schedule';
      setError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Schedule' : 'Create Schedule'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} mt={1}>
          <TextField
            label="Schedule name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            placeholder="e.g., Daily sync"
          />

          <CronExpressionInput value={cron} onChange={setCron} />

          <FormControl size="small" fullWidth>
            <InputLabel>Staging mode</InputLabel>
            <Select
              value={stagingMode}
              label="Staging mode"
              onChange={(e) => setStagingMode(e.target.value)}
            >
              <MenuItem value="auto">Auto (export immediately)</MenuItem>
              <MenuItem value="manual">Manual (require approval)</MenuItem>
            </Select>
          </FormControl>

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
